import React from "react";
import { useNavigate } from "react-router-dom";
import { Shield, Lock, Eye, Database } from "lucide-react";
import AdBanner from "../components/AdBanner";
import useSeo from "../seo/useSeo";

export default function PrivacyPolicy() {
  useSeo("/privacy-policy");
  const navigate = useNavigate();

  return (
    <div className="min-h-[100dvh] bg-slate-950 text-white pb-24">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : navigate("/")}
            className="p-2 -ml-2 rounded-full hover:bg-slate-800 transition-colors"
          >
            ←
          </button>
          <h1 className="text-lg font-bold">Privacy Policy</h1>
        </div>
      </div>

      <div className="p-5 space-y-8 max-w-2xl mx-auto">
        <div className="text-center py-6">
          <div className="w-16 h-16 bg-primary/20 text-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield size={32} />
          </div>
          <h2 className="text-2xl font-black">Your Privacy is our Priority</h2>
          <p className="text-slate-400 mt-2 text-sm">Last Updated: April 2026</p>
        </div>

        <div className="space-y-6">
          <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
            <div className="flex items-center gap-3 text-primary mb-3">
              <Database size={20} />
              <h3 className="font-bold text-lg">1. Data We Collect</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              <strong>CarTrack collects location data to enable real-time vehicle tracking, even when the app is closed or not in use.</strong> This live GPS location data (latitude, longitude, speed) is essential to provide our tracking services. Your Owner Dashboard account requires an email address and name (either via manual sign-up or Google Sign-In) for authentication purposes. We also securely store your mandatory Telegram Chat ID to ensure we can deliver important alerts.
            </p>
          </div>

          <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
            <div className="flex items-center gap-3 text-primary mb-3">
              <Eye size={20} />
              <h3 className="font-bold text-lg">2. How We Use Your Data</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-3">
              The primary purpose of collecting location data is to provide you with the tracking service you requested. We use this data to:
            </p>
            <ul className="list-disc pl-5 text-slate-400 text-sm space-y-2">
              <li>Display your car's live location on your dashboard.</li>
              <li>Trigger Geofence and Overspeed alerts.</li>
              <li>Dispatch SOS emergency messages to your Telegram contacts.</li>
            </ul>
          </div>

          <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
            <div className="flex items-center gap-3 text-primary mb-3">
              <Lock size={20} />
              <h3 className="font-bold text-lg">3. Data Security</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              We implement strict industry-standard security measures. Your live location is transmitted securely over HTTPS. We do NOT sell your location data to third-party data brokers.
            </p>
          </div>

          <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
            <div className="flex items-center gap-3 text-primary mb-3">
              <Eye size={20} />
              <h3 className="font-bold text-lg">4. Advertising</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              Our Car Head Unit App uses Google AdMob to display advertisements. AdMob may collect and use certain data, such as device identifiers and location data, to provide personalized ads. This complies with Google's Advertising Policies and EU User Consent Policies.
            </p>
          </div>

          <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800">
            <div className="flex items-center gap-3 text-red-500 mb-3">
              <Database size={20} />
              <h3 className="font-bold text-lg">5. Data Deletion</h3>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              You have the right to request the complete deletion of your data at any time. You can do this directly within the Owner Dashboard by navigating to <strong>Settings</strong> &gt; <strong>Delete Account</strong>. This will permanently erase your account, tracked locations, vehicles, and SOS history from our servers.
            </p>
          </div>

          <div className="pt-4">
            <AdBanner testid="privacy-ad-banner" />
          </div>
        </div>
      </div>
    </div>
  );
}
